import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import {
  Rocket, Package, Store, Instagram as IgIcon, UserPlus, ArrowRight, CheckCircle2,
  MessageCircle, Facebook as FbIcon, Settings as SetIcon, Inbox as InboxIcon, LayoutGrid,
  AlertTriangle, Sun, Moon, Gift, Info, Boxes, ShoppingBag, ShoppingCart, Search,
  Send, Plug, CreditCard, Users2, Bell, ChevronDown, Check, Shield, X, Sparkles,
  TrendingUp, Tag, HelpCircle, LogOut, Menu, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

function bizId() {
  if (typeof window === 'undefined') return 'default';
  return localStorage.getItem('wamerce-business') || 'default';
}

// Wrapped fetch that auto-adds the X-Wamerce-Business-Id header (scoped multi-tenant).
function apiFetch(path, opts = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'X-Wamerce-Business-Id': bizId(),
      ...(opts.headers || {}),
    },
  });
}

// Shoplinx-style grouped sidebar navigation
const NAV_GROUPS = [
  {
    label: null,
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: LayoutGrid, to: '/v2/dashboard' },
    ],
  },
  {
    label: 'Inbox',
    items: [
      { key: 'inbox', label: 'All Messages', icon: InboxIcon, to: '/v2/inbox' },
      { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, to: '/v2/whatsapp', channel: 'whatsapp' },
      { key: 'instagram', label: 'Instagram', icon: IgIcon, to: '/v2/instagram', channel: 'instagram' },
      { key: 'facebook', label: 'Facebook', icon: FbIcon, to: '/v2/facebook', channel: 'facebook' },
    ],
  },
  {
    label: 'Store',
    items: [
      { key: 'inventory', label: 'Inventory', icon: Package, to: '/v2/inventory' },
      { key: 'others', label: 'Marketing', icon: TrendingUp, to: '/v2/others' },
    ],
  },
  {
    label: 'More',
    items: [
      { key: 'settings', label: 'Settings', icon: SetIcon, to: '/v2/settings' },
    ],
  },
];

const CHANNEL_COLORS = {
  whatsapp: { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-500/30' },
  instagram: { bg: 'bg-gradient-to-br from-fuchsia-500 via-rose-500 to-amber-500', text: 'text-rose-600', light: 'bg-rose-50', border: 'border-rose-200', ring: 'ring-rose-500/30' },
  facebook: { bg: 'bg-blue-600', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-200', ring: 'ring-blue-500/30' },
};

const NOTIF_ICONS = { MessageCircle, Rocket, Shield, Bell };

const TINTS = {
  sky: 'bg-sky-500 text-white',
  emerald: 'bg-emerald-500 text-white',
  rose: 'bg-gradient-to-br from-fuchsia-500 to-rose-500 text-white',
  violet: 'bg-violet-500 text-white',
  amber: 'bg-amber-500 text-white',
};

const STEP_ICON = {
  'connect-shopify': ShoppingCart,
  'add-products': Package,
  'setup-store': Store,
  'connect-instagram': IgIcon,
  'add-team': UserPlus,
};

function useTheme() {
  const [dark, setDark] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem('wamerce-theme') === 'dark'
  );
  useEffect(() => {
    if (dark) document.documentElement.classList.add('wamerce-dark');
    else document.documentElement.classList.remove('wamerce-dark');
    localStorage.setItem('wamerce-theme', dark ? 'dark' : 'light');
  }, [dark]);
  return [dark, setDark];
}

export function WamerceShell({ active = 'dashboard', children }) {
  const [dark, setDark] = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[#f7f8fb] wamerce-dark:bg-[#0b0d18] text-slate-800 wamerce-dark:text-slate-200">
      {/* ============ LEFT SIDEBAR ============ */}
      {mobileOpen && <div onClick={() => setMobileOpen(false)} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden" />}
      <aside data-testid="wamerce-sidebar"
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-[260px] shrink-0 bg-white wamerce-dark:bg-[#11131e] border-r border-slate-200 wamerce-dark:border-slate-800 flex flex-col transition-transform ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}>
        {/* Logo */}
        <div className="h-16 px-5 flex items-center gap-2.5 border-b border-slate-100 wamerce-dark:border-slate-800">
          <span className="relative w-9 h-9 inline-flex items-center justify-center">
            <span className="absolute inset-0 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500" />
            <Sparkles className="relative w-4 h-4 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold tracking-tight text-slate-900 wamerce-dark:text-white leading-none">Wamerce</p>
            <p className="text-[10px] text-emerald-600 font-semibold tracking-wider uppercase mt-0.5">AI Sales OS</p>
          </div>
          <button type="button" onClick={() => setMobileOpen(false)} className="lg:hidden text-slate-400" aria-label="Close menu"><X className="w-5 h-5" /></button>
        </div>

        {/* Business Switcher */}
        <div className="px-3 pt-3"><BusinessSwitcher /></div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5" data-testid="wamerce-sidenav">
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi} className="space-y-1">
              {group.label && (
                <p className="px-3 text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400 wamerce-dark:text-slate-500 mb-1.5">
                  {group.label}
                </p>
              )}
              {group.items.map((n) => {
                const Icon = n.icon;
                const isActive = n.key === active;
                const channel = n.channel && CHANNEL_COLORS[n.channel];
                return (
                  <Link key={n.key} to={n.to} data-testid={`sidenav-${n.key}`}
                    onClick={() => setMobileOpen(false)}
                    className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-emerald-50 text-emerald-700 wamerce-dark:bg-emerald-500/15 wamerce-dark:text-emerald-300 font-semibold shadow-sm shadow-emerald-100/50'
                        : 'text-slate-600 wamerce-dark:text-slate-400 hover:bg-slate-50 wamerce-dark:hover:bg-slate-800/40 hover:text-slate-900 wamerce-dark:hover:text-white'
                    }`}>
                    <span className={`w-7 h-7 rounded-lg inline-flex items-center justify-center shrink-0 transition-colors ${
                      isActive ? 'bg-emerald-500 text-white shadow-sm'
                               : channel ? `${channel.light} ${channel.text} group-hover:scale-110`
                               : 'bg-slate-100 wamerce-dark:bg-slate-800 text-slate-500 wamerce-dark:text-slate-400 group-hover:bg-slate-200 wamerce-dark:group-hover:bg-slate-700'
                    }`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="flex-1 truncate">{n.label}</span>
                    {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Bottom: Trial CTA */}
        <div className="p-3 border-t border-slate-100 wamerce-dark:border-slate-800">
          <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-4 text-white shadow-md">
            <div className="flex items-center gap-2 mb-1.5">
              <Gift className="w-4 h-4" />
              <span className="text-[10px] font-bold tracking-wider uppercase">Free Forever</span>
            </div>
            <p className="text-xs leading-snug text-white/90 mb-3">Get 500 free contacts & WhatsApp messaging.</p>
            <button type="button" data-testid="sidebar-trial-cta" className="w-full h-9 rounded-lg bg-white text-emerald-700 font-bold text-xs hover:bg-emerald-50 transition-colors">
              Upgrade Plan →
            </button>
          </div>
        </div>
      </aside>

      {/* ============ MAIN AREA ============ */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 wamerce-dark:bg-[#11131e]/80 backdrop-blur-md border-b border-slate-200 wamerce-dark:border-slate-800">
          <div className="h-full px-5 lg:px-8 flex items-center gap-3">
            <button type="button" onClick={() => setMobileOpen(true)} data-testid="menu-toggle" className="lg:hidden p-2 -ml-2 text-slate-500 hover:text-slate-700">
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden md:flex relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="topbar-search" placeholder="Search conversations, orders, customers..."
                className="w-full h-10 pl-10 pr-3 rounded-xl bg-slate-100 wamerce-dark:bg-slate-800/60 border border-transparent focus:bg-white wamerce-dark:focus:bg-slate-800 focus:border-emerald-300 focus:outline-none text-sm placeholder:text-slate-400 transition-colors" />
            </div>
            <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
              <span className="hidden sm:inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 wamerce-dark:bg-emerald-500/10 wamerce-dark:text-emerald-300 wamerce-dark:border-emerald-500/30">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
              </span>
              <NotificationsDropdown />
              <button type="button" onClick={() => setDark(!dark)} data-testid="theme-toggle" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 wamerce-dark:hover:bg-slate-800 wamerce-dark:text-slate-400">
                {dark ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
              </button>
              <div className="relative">
                <img src="https://ui-avatars.com/api/?name=TNV+Admin&background=10b981&color=fff&size=64&bold=true" alt="profile" className="w-9 h-9 rounded-full ring-2 ring-emerald-100" />
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
              </div>
            </div>
          </div>
        </header>

        <PromoBanner />
        <main className="flex-1 px-5 lg:px-8 py-6 lg:py-8 max-w-[1600px] w-full">{children}</main>
      </div>

      <style>{`
        :root.wamerce-dark { color-scheme: dark; }
        .wamerce-dark .wamerce-dark\\:bg-\\[\\#0b0d18\\] { background:#0b0d18; }
        .wamerce-dark .wamerce-dark\\:bg-\\[\\#11131e\\] { background:#11131e; }
        .wamerce-dark .wamerce-dark\\:bg-\\[\\#161826\\] { background:#161826; }
        .wamerce-dark .wamerce-dark\\:bg-\\[\\#1e2030\\] { background:#1e2030; }
      `}</style>
    </div>
  );
}

function useClickOutside(ref, onClose) {
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

function BusinessSwitcher() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([{ id: 'default', name: 'My Workspace', platform: 'wamerce' }]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem('wamerce-business') || 'default');
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  useEffect(() => {
    apiFetch("/api/v2/businesses").then(r => r.json()).then(d => {
      if (d.businesses?.length) {
        setItems(d.businesses);
        if (!d.businesses.find(b => b.id === activeId)) setActiveId(d.businesses[0].id);
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const active = items.find(b => b.id === activeId) || items[0];
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(v => !v)} data-testid="business-switcher"
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-slate-50 wamerce-dark:hover:bg-slate-800/40 transition-colors group">
        <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 via-fuchsia-500 to-rose-500 text-white text-xs font-bold inline-flex items-center justify-center shadow-sm shrink-0">
          {(active?.name || '?').slice(0, 2).toUpperCase()}
        </span>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold leading-none mb-0.5">Workspace</p>
          <p className="text-sm font-bold tracking-tight text-slate-900 wamerce-dark:text-white truncate">{active?.name}</p>
        </div>
        <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-slate-700 transition-colors shrink-0" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 rounded-xl bg-white wamerce-dark:bg-[#1e2030] border border-slate-200 wamerce-dark:border-slate-800 shadow-xl py-2 z-30" data-testid="business-menu">
          <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Switch workspace</p>
          {items.map(b => (
            <button key={b.id} type="button" onClick={() => { setActiveId(b.id); localStorage.setItem('wamerce-business', b.id); setOpen(false); window.location.reload(); }}
              data-testid={`business-${b.id}`}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 wamerce-dark:hover:bg-slate-800/60 text-left">
              <span className="w-7 h-7 rounded-md bg-gradient-to-br from-violet-400 to-rose-400 text-white text-[10px] font-bold inline-flex items-center justify-center">{(b.name || '?').slice(0, 2).toUpperCase()}</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900 wamerce-dark:text-white truncate">{b.name}</p>
                <p className="text-[10px] text-slate-500 capitalize">{b.platform}</p>
              </div>
              {b.id === activeId && <Check className="w-4 h-4 text-emerald-500" />}
            </button>
          ))}
          <div className="border-t border-slate-100 wamerce-dark:border-slate-800 mt-1 pt-1">
            <Link to="/v2/setup/connect-shopify" onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-emerald-600 hover:bg-emerald-50/60 wamerce-dark:hover:bg-emerald-500/10">
              <Plug className="w-4 h-4" /> Connect new store
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationsDropdown() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false));
  const load = () => {
    apiFetch("/api/v2/notifications").then(r => r.json()).then(d => {
      setItems(d.notifications || []);
      setUnread(d.unread_count || 0);
    }).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(v => !v)} data-testid="notifications-btn"
        className="relative text-slate-500 hover:text-slate-700 wamerce-dark:text-slate-400 wamerce-dark:hover:text-white p-1">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold inline-flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-xl bg-white wamerce-dark:bg-[#1e2030] border border-slate-200 wamerce-dark:border-slate-800 shadow-xl z-30 overflow-hidden" data-testid="notifications-menu">
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100 wamerce-dark:border-slate-800">
            <h3 className="font-semibold text-slate-900 wamerce-dark:text-white">Notifications</h3>
            <button type="button" onClick={() => setItems(items.map(n => ({ ...n, unread: false })))} className="text-xs text-emerald-600 hover:underline">Mark all read</button>
          </header>
          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-8">You&apos;re all caught up ✨</p>
            ) : items.map(n => {
              const Icon = NOTIF_ICONS[n.icon] || Bell;
              const tintMap = { emerald: 'bg-emerald-100 text-emerald-600', violet: 'bg-violet-100 text-violet-600', amber: 'bg-amber-100 text-amber-600' };
              return (
                <div key={n.id} data-testid={`notif-${n.id}`}
                  className={`flex gap-3 px-4 py-3 border-b border-slate-100 wamerce-dark:border-slate-800/60 hover:bg-slate-50 wamerce-dark:hover:bg-slate-800/40 ${n.unread ? 'bg-emerald-50/30' : ''}`}>
                  <span className={`w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0 ${tintMap[n.tint] || tintMap.violet}`}><Icon className="w-4 h-4" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900 wamerce-dark:text-white">{n.title}</p>
                    <p className="text-xs text-slate-500 line-clamp-2">{n.body}</p>
                  </div>
                  {n.unread && <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0 mt-2" />}
                </div>
              );
            })}
          </div>
          <Link to="/v2/settings" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-center text-xs text-emerald-600 hover:underline border-t border-slate-100 wamerce-dark:border-slate-800">View all activity</Link>
        </div>
      )}
    </div>
  );
}

function PromoBanner() {
  const [closed, setClosed] = useState(() => typeof window !== 'undefined' && localStorage.getItem('wamerce-promo-closed') === '1');
  if (closed) return null;
  return (
    <div data-testid="promo-banner" className="bg-[#101635] text-white text-sm">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-12 flex items-center gap-2 sm:gap-4 overflow-hidden">
        <span className="hidden sm:inline-flex items-center gap-1 h-6 px-2 rounded-md bg-violet-500/20 text-violet-200 text-[11px] font-bold tracking-wider shrink-0">LIMITED</span>
        <Gift className="w-4 h-4 text-amber-300 shrink-0" />
        <p className="flex-1 truncate text-slate-200">
          <span className="font-semibold">Free plan · 500 contacts.</span>{' '}
          <span className="hidden sm:inline">Start your 7-day full-access trial and unlock unlimited replies + premium automations.</span>
          <span className="sm:hidden">Upgrade for unlimited replies.</span>
        </p>
        <button type="button" data-testid="start-trial-btn" className="hidden md:inline-flex items-center gap-2 h-8 px-3.5 rounded-md bg-emerald-400 hover:bg-emerald-300 text-emerald-950 font-semibold text-sm shrink-0">
          Start 7-day trial <ArrowRight className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => { localStorage.setItem('wamerce-promo-closed', '1'); setClosed(true); }} className="text-slate-400 hover:text-white shrink-0" aria-label="Dismiss">×</button>
      </div>
    </div>
  );
}

// ============ DASHBOARD ============
export default function WamerceDashboardV2() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: 'TNV Admin', profile_verified: false });
  const [setup, setSetup] = useState({ progress: 0, steps: [], summary: {}, integrations: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/users/login?username=admin&password=admin`, { method: 'POST' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.user) setProfile((p) => ({ ...p, name: d.user.full_name || d.user.username || p.name })); })
      .catch(() => {});
    apiFetch("/api/v2/setup-status")
      .then((r) => r.json())
      .then((d) => setSetup(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <WamerceShell active="dashboard">
      {!profile.profile_verified && (
        <div data-testid="profile-alert" className="rounded-xl bg-amber-50 wamerce-dark:bg-amber-500/10 border border-amber-200 wamerce-dark:border-amber-500/30 px-5 py-3.5 flex items-center gap-3 mb-6">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="flex-1 text-sm font-medium text-amber-800 wamerce-dark:text-amber-200">Please complete your profile. Verify your email and phone number.</p>
          <Button size="sm" data-testid="verify-profile-btn" className="h-9 bg-emerald-400 hover:bg-emerald-300 text-emerald-950 font-semibold rounded-md">Verify email &amp; phone</Button>
        </div>
      )}

      <header className="space-y-1 mb-7">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 wamerce-dark:text-white">Welcome back, {profile.name} <span aria-hidden>👋</span></h1>
        <p className="text-slate-500 wamerce-dark:text-slate-400">Let&apos;s complete your setup to unlock all features and start growing your business.</p>
      </header>

      <SetupCard setup={setup} loading={loading} onStepClick={(id) => navigate(`/v2/setup/${id}`)} />

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <UserManagementCard team_count={setup.summary?.team_count || 1} team_active={setup.summary?.team_active || 0} />
        <StoreSetupCard stores_count={setup.summary?.stores_count || 0} onSetup={() => navigate('/v2/setup/setup-store')} />
        <InventoryCard products_count={setup.summary?.products_count || 0} onAdd={() => navigate('/v2/inventory')} />
      </div>
    </WamerceShell>
  );
}

function SetupCard({ setup, loading, onStepClick }) {
  const steps = setup.steps || [];
  const done = steps.filter((s) => s.completed).length;
  return (
    <section data-testid="setup-card" className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 p-6">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <span className="inline-flex w-10 h-10 rounded-xl bg-emerald-50 wamerce-dark:bg-emerald-500/15 items-center justify-center">
            <Rocket className="w-5 h-5 text-emerald-600 wamerce-dark:text-emerald-300" />
          </span>
          <h2 className="text-lg font-semibold text-slate-900 wamerce-dark:text-white">Complete Your Setup</h2>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500 wamerce-dark:text-slate-400">Overall Progress</p>
          <p className="text-3xl font-bold text-emerald-500" data-testid="setup-progress-percent">{loading ? '…' : `${setup.progress || 0}%`}</p>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500 wamerce-dark:text-slate-400 mb-2">
        <span>Setup Progress</span>
        <span>{done}/{steps.length || 5}</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-slate-100 wamerce-dark:bg-slate-800 mb-7 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all" style={{ width: `${setup.progress || 0}%` }} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {(steps.length ? steps : Array(5).fill({})).map((step, i) => (
          <SetupStep key={step.id || i} step={step} onClick={() => step.id && onStepClick(step.id)} />
        ))}
      </div>
    </section>
  );
}

function SetupStep({ step, onClick }) {
  const Icon = STEP_ICON[step.id] || Rocket;
  const completed = !!step.completed;
  return (
    <button type="button" data-testid={`setup-step-${step.id || 'placeholder'}`} onClick={onClick}
      className={`group text-left rounded-xl border p-5 flex flex-col gap-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        completed ? 'border-emerald-400/60 bg-emerald-50/40 wamerce-dark:bg-emerald-500/5'
                  : 'border-slate-200 wamerce-dark:border-slate-800 bg-white wamerce-dark:bg-[#1e2030]'
      }`}>
      <span className={`w-12 h-12 rounded-full inline-flex items-center justify-center shadow-md ${TINTS[step.tint] || TINTS.emerald}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div className="flex-1 space-y-1.5">
        <h3 className="font-semibold text-slate-900 wamerce-dark:text-white text-[15px]">{step.title || '—'}</h3>
        <p className="text-[13px] leading-relaxed text-slate-500 wamerce-dark:text-slate-400">{step.description || ''}</p>
      </div>
      <div className="pt-1 inline-flex items-center gap-1.5 text-sm font-semibold">
        {completed ? (
          <span className="text-emerald-600 wamerce-dark:text-emerald-400 inline-flex items-center gap-1.5">Manage <CheckCircle2 className="w-4 h-4" /></span>
        ) : (
          <span className="text-emerald-600 wamerce-dark:text-emerald-400 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Start <ArrowRight className="w-4 h-4" /></span>
        )}
      </div>
    </button>
  );
}

function StatusCard({ count, status, statusColor, icon, title, subtitle, children }) {
  return (
    <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 p-6 space-y-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-400 wamerce-dark:text-slate-500">{count}</span>
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${statusColor}`}>
          {status === 'Live' && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}{status}
        </span>
      </div>
      <div className="flex items-center gap-3">{icon}<div><h3 className="font-semibold text-slate-900 wamerce-dark:text-white text-base">{title}</h3><p className="text-xs text-slate-500 wamerce-dark:text-slate-400">{subtitle}</p></div></div>
      {children}
    </section>
  );
}

function UserManagementCard({ team_count, team_active }) {
  return (
    <StatusCard count={`${team_count} / ${team_count}`} status="Live" statusColor="text-emerald-600 wamerce-dark:text-emerald-400"
      icon={<span className="inline-flex w-12 h-12 rounded-xl bg-slate-100 wamerce-dark:bg-slate-800 items-center justify-center"><UserPlus className="w-5 h-5 text-slate-500" /></span>}
      title="User Management" subtitle="Manage team and permissions">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-sky-50 wamerce-dark:bg-sky-500/10 p-4 text-center">
          <p className="text-2xl font-bold text-sky-600 wamerce-dark:text-sky-300">{team_count}</p>
          <p className="text-xs text-slate-500 wamerce-dark:text-slate-400 mt-1">Total Agents</p>
        </div>
        <div className="rounded-xl bg-emerald-50 wamerce-dark:bg-emerald-500/10 p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600 wamerce-dark:text-emerald-300">{team_active}</p>
          <p className="text-xs text-slate-500 wamerce-dark:text-slate-400 mt-1">Active Now</p>
        </div>
      </div>
    </StatusCard>
  );
}

function EmptyHint({ items }) {
  return (
    <ul className="space-y-1.5 text-sm">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2 text-slate-600 wamerce-dark:text-slate-300">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {it}
        </li>
      ))}
    </ul>
  );
}

function StoreSetupCard({ stores_count, onSetup }) {
  const ok = stores_count > 0;
  return (
    <StatusCard count={`${ok ? stores_count : 2} / 9`} status={ok ? 'Live' : 'Not Set Up'} statusColor={ok ? 'text-emerald-600' : 'text-rose-500'}
      icon={<span className="inline-flex w-12 h-12 rounded-xl bg-orange-100 wamerce-dark:bg-orange-500/15 items-center justify-center"><Store className="w-5 h-5 text-orange-500" /></span>}
      title="Store Setup" subtitle="Configure your online store">
      {ok ? (
        <div className="rounded-xl bg-emerald-50/50 wamerce-dark:bg-emerald-500/5 p-6 text-center space-y-2">
          <Store className="w-10 h-10 mx-auto text-emerald-500" />
          <p className="font-semibold text-slate-700 wamerce-dark:text-slate-200">{stores_count} store{stores_count > 1 ? 's' : ''} connected</p>
          <p className="text-xs text-slate-500 wamerce-dark:text-slate-400">Sync runs every 15 minutes</p>
        </div>
      ) : (
        <div className="rounded-xl bg-slate-50 wamerce-dark:bg-slate-800/40 p-6 text-center space-y-2">
          <Store className="w-10 h-10 mx-auto text-slate-300" />
          <p className="font-semibold text-slate-700 wamerce-dark:text-slate-200">Store Not Set Up</p>
          <p className="text-xs text-slate-500 wamerce-dark:text-slate-400">Configure your store theme, logo, and settings to start selling</p>
        </div>
      )}
      <div className="rounded-lg bg-amber-50 wamerce-dark:bg-amber-500/10 p-3">
        <p className="text-xs font-semibold text-amber-800 wamerce-dark:text-amber-200 mb-1.5">What you&apos;ll get:</p>
        <EmptyHint items={['Professional storefront', 'Custom branding', 'Mobile-ready checkout']} />
      </div>
      <Button onClick={onSetup} data-testid="setup-store-btn" className="w-full h-10 bg-orange-500 hover:bg-orange-400 text-white font-semibold rounded-lg">
        {ok ? 'Manage stores' : 'Set up store'}
      </Button>
    </StatusCard>
  );
}

function InventoryCard({ products_count, onAdd }) {
  const ok = products_count > 0;
  return (
    <StatusCard count={`${products_count} / ${Math.max(3, products_count)}`} status={ok ? 'Live' : 'Not Set Up'} statusColor={ok ? 'text-emerald-600' : 'text-rose-500'}
      icon={<span className="inline-flex w-12 h-12 rounded-xl bg-emerald-100 wamerce-dark:bg-emerald-500/15 items-center justify-center"><ShoppingBag className="w-5 h-5 text-emerald-600" /></span>}
      title="Inventory" subtitle="Products, Categories & Brands">
      {ok ? (
        <div className="rounded-xl bg-emerald-50/50 wamerce-dark:bg-emerald-500/5 p-6 text-center space-y-2">
          <ShoppingBag className="w-10 h-10 mx-auto text-emerald-500" />
          <p className="font-semibold text-slate-700 wamerce-dark:text-slate-200">{products_count.toLocaleString()} products synced</p>
          <p className="text-xs text-slate-500 wamerce-dark:text-slate-400">Last sync: just now</p>
        </div>
      ) : (
        <div className="rounded-xl bg-slate-50 wamerce-dark:bg-slate-800/40 p-6 text-center space-y-2">
          <Boxes className="w-10 h-10 mx-auto text-slate-300" />
          <p className="font-semibold text-slate-700 wamerce-dark:text-slate-200">No Products Added</p>
          <p className="text-xs text-slate-500 wamerce-dark:text-slate-400">Add products to enable AI chatbot support and start selling</p>
        </div>
      )}
      <div className="rounded-lg bg-emerald-50 wamerce-dark:bg-emerald-500/10 p-3">
        <p className="text-xs font-semibold text-emerald-800 wamerce-dark:text-emerald-300 mb-1.5">Why add products?</p>
        <EmptyHint items={['Enable AI chatbot recommendations', 'Show in-stock catalog', 'Auto-sync from Shopify']} />
      </div>
      <Button onClick={onAdd} data-testid="add-products-btn" className="w-full h-10 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg">
        {ok ? 'Browse inventory' : 'Add products'}
      </Button>
    </StatusCard>
  );
}

// ============ INBOX (live data, Shoplinx-style) ============
export function WamerceInbox() {
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [channelFilter, setChannelFilter] = useState('all'); // all|whatsapp|instagram|facebook
  const [statusFilter, setStatusFilter] = useState('all');   // all|unread|read|closed
  const [aiOn, setAiOn] = useState(false);

  const loadConvs = () => {
    apiFetch("/api/v2/inbox/conversations").then(r => r.json()).then(d => {
      const list = d.conversations || [];
      setConversations(list);
      setActive(prev => prev && list.find(c => c.phone === prev.phone) ? prev : list[0] || null);
    }).catch(() => {});
  };
  const loadMessages = (phone) => {
    if (!phone) return;
    apiFetch(`/api/v2/inbox/messages/${encodeURIComponent(phone)}`).then(r => r.json()).then(d => {
      setMessages(d.messages || []);
    }).catch(() => {});
  };
  useEffect(() => { loadConvs(); const t = setInterval(loadConvs, 15000); return () => clearInterval(t); }, []);
  useEffect(() => { if (active?.phone) loadMessages(active.phone); }, [active?.phone]);

  const send = async () => {
    if (!reply.trim() || !active?.phone) return;
    setSending(true);
    try {
      const res = await apiFetch("/api/v2/inbox/send", {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: active.phone, text: reply.trim() }),
      }).then(r => r.json());
      if (res.delivered === false && res.error) toast.error(res.error.slice(0, 100));
      else toast.success('Message sent');
      setReply('');
      loadMessages(active.phone);
      loadConvs();
    } catch (e) { toast.error(e.message || 'Send failed'); } finally { setSending(false); }
  };

  const fmtTime = (s) => s ? new Date(s * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  const fmtAgo = (s) => {
    if (!s) return '';
    const diff = Date.now() / 1000 - s;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff/60)}m`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h`;
    return `${Math.floor(diff/86400)}d`;
  };

  // Channel & status filters (mock channel for now — only WA available)
  const filtered = conversations.filter((c) => {
    if (statusFilter === 'unread' && !c.unread) return false;
    if (statusFilter === 'read' && c.unread) return false;
    // channel filter is informational since we only have WhatsApp data
    return true;
  });

  const CHANNEL_FILTERS = [
    { key: 'all', label: 'All', count: conversations.length },
    { key: 'whatsapp', label: 'WhatsApp', count: conversations.length, dot: 'bg-emerald-500' },
    { key: 'instagram', label: 'Instagram', count: 0, dot: 'bg-rose-500' },
    { key: 'facebook', label: 'Facebook', count: 0, dot: 'bg-blue-500' },
  ];

  return (
    <WamerceShell active="inbox">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 wamerce-dark:text-white">Unified Inbox</h1>
          <p className="text-sm text-slate-500 wamerce-dark:text-slate-400">All your customer conversations across WhatsApp, Instagram & Facebook.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setAiOn(!aiOn)} data-testid="ai-toggle"
            className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-xs font-bold border transition-all shadow-sm ${
              aiOn
                ? 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border-amber-300 hover:shadow-md'
                : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
            }`}>
            <Zap className={`w-3.5 h-3.5 ${aiOn ? 'fill-amber-500 text-amber-600' : ''}`} />
            AI Reply {aiOn ? 'On' : 'Off'}
          </button>
        </div>
      </header>

      {/* Channel filter pills */}
      <div className="flex items-center gap-1.5 mb-4 p-1 bg-slate-100 wamerce-dark:bg-slate-800/60 rounded-xl w-fit" data-testid="channel-filters">
        {CHANNEL_FILTERS.map((cf) => (
          <button key={cf.key} type="button" onClick={() => setChannelFilter(cf.key)} data-testid={`channel-${cf.key}`}
            className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-xs font-semibold transition-all ${
              channelFilter === cf.key
                ? 'bg-white wamerce-dark:bg-slate-700 text-slate-900 wamerce-dark:text-white shadow-sm'
                : 'text-slate-600 wamerce-dark:text-slate-400 hover:text-slate-900 wamerce-dark:hover:text-white'
            }`}>
            {cf.dot && <span className={`w-1.5 h-1.5 rounded-full ${cf.dot}`} />}
            {cf.label}
            <span className="text-[10px] font-bold opacity-60">{cf.count}</span>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[320px_1fr_300px] gap-4 lg:h-[calc(100vh-280px)] min-h-[520px]">
        <aside className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 flex flex-col overflow-hidden">
          {/* Status sub-filter */}
          <div className="p-3 border-b border-slate-100 wamerce-dark:border-slate-800 space-y-2.5">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search conversations" className="pl-9 h-9 bg-slate-50 wamerce-dark:bg-slate-800/60 border-slate-200 wamerce-dark:border-slate-700 text-sm" />
            </div>
            <div className="flex items-center gap-1 text-xs" data-testid="status-filters">
              {['all', 'unread', 'read', 'closed'].map((s) => (
                <button key={s} type="button" onClick={() => setStatusFilter(s)} data-testid={`status-${s}`}
                  className={`px-2 py-1 rounded-md font-semibold capitalize transition-colors ${
                    statusFilter === s ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500 hover:bg-slate-50'
                  }`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100 wamerce-dark:divide-slate-800">
            {filtered.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-400">
                <InboxIcon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                No conversations yet.
                <p className="text-xs mt-1">Send a WhatsApp message and replies will appear here.</p>
              </div>
            ) : filtered.map((c) => (
              <button key={c.phone} type="button" onClick={() => setActive(c)} data-testid={`conv-${c.phone}`}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${active?.phone === c.phone ? 'bg-emerald-50/60 wamerce-dark:bg-emerald-500/10' : 'hover:bg-slate-50 wamerce-dark:hover:bg-slate-800/40'}`}>
                <div className="relative shrink-0">
                  <span className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white text-sm font-bold inline-flex items-center justify-center shadow-sm">{(c.phone || '?').slice(-2)}</span>
                  <ChannelDot channel="whatsapp" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2"><p className="font-semibold text-slate-900 wamerce-dark:text-white text-sm truncate">+{c.phone}</p><span className="text-[11px] text-slate-400">{fmtAgo(c.last_at)}</span></div>
                  <p className="text-xs text-slate-500 wamerce-dark:text-slate-400 truncate">{c.last_direction === 'outgoing' ? '→ ' : ''}{c.last || 'No content'}</p>
                </div>
                {c.unread > 0 && <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold shrink-0">{c.unread}</span>}
              </button>
            ))}
          </div>
        </aside>

        <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 flex flex-col overflow-hidden">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm gap-3 p-10">
              <span className="w-14 h-14 rounded-2xl bg-emerald-50 inline-flex items-center justify-center"><MessageCircle className="w-6 h-6 text-emerald-500" /></span>
              <p className="font-semibold text-slate-700">Select a conversation</p>
              <p className="text-xs text-center max-w-xs">Pick a chat from the left to view messages and reply.</p>
            </div>
          ) : (
            <>
              <header className="p-4 border-b border-slate-200 wamerce-dark:border-slate-800 flex items-center gap-3">
                <div className="relative">
                  <span className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white font-bold inline-flex items-center justify-center">{(active.phone || '?').slice(-2)}</span>
                  <ChannelDot channel="whatsapp" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900 wamerce-dark:text-white">+{active.phone}</p>
                  <p className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Online · WhatsApp</p>
                </div>
                {aiOn && (
                  <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-bold" data-testid="ai-active-pill">
                    <Zap className="w-3 h-3 fill-amber-500" /> AI Assist
                  </span>
                )}
              </header>
              <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-slate-50/50 wamerce-dark:bg-slate-900/40" data-testid="messages-pane">
                {messages.length === 0 ? <p className="text-center text-sm text-slate-400 py-10">No messages yet in this thread.</p> :
                  messages.map((m, i) => (
                    <div key={m.message_id || i} data-testid={`msg-${i}`}>
                      <Bubble side={m.direction === 'outgoing' ? 'me' : 'them'}
                        text={m.text_body || (m.template_name ? `Template · ${m.template_name}` : '—')}
                        meta={`${fmtTime(m.sent_at)} · ${m.status || 'sent'}`} />
                    </div>
                  ))
                }
              </div>
              <footer className="p-3 border-t border-slate-200 wamerce-dark:border-slate-800 flex items-center gap-2">
                {aiOn && (
                  <span className="inline-flex items-center gap-1 h-9 px-2.5 rounded-lg bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border border-amber-200 text-[11px] font-bold shrink-0">
                    <Zap className="w-3 h-3" /> AI
                  </span>
                )}
                <Input value={reply} onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !sending) send(); }}
                  placeholder={aiOn ? "AI will suggest a reply… or type your own" : "Type your reply…"}
                  className="flex-1 h-10 bg-slate-50 wamerce-dark:bg-slate-800/60 border-slate-200 wamerce-dark:border-slate-700" data-testid="inbox-reply-input" />
                <Button onClick={send} disabled={sending || !reply.trim()} className="h-10 bg-emerald-500 hover:bg-emerald-400 text-white" data-testid="inbox-send-btn"><Send className="w-4 h-4" /></Button>
              </footer>
            </>
          )}
        </section>

        <aside className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 p-5 hidden lg:block">
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100 wamerce-dark:border-slate-800">
            <span className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white font-bold inline-flex items-center justify-center text-base">{(active?.phone || '?').slice(-2)}</span>
            <div>
              <p className="font-semibold text-slate-900 wamerce-dark:text-white">+{active?.phone || '—'}</p>
              <p className="text-xs text-slate-500">WhatsApp Customer</p>
            </div>
          </div>
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">Details</h3>
          <dl className="space-y-3 text-sm">
            <div><dt className="text-xs text-slate-500">Channel</dt><dd className="font-medium text-slate-900 wamerce-dark:text-white">WhatsApp</dd></div>
            <div><dt className="text-xs text-slate-500">Messages exchanged</dt><dd className="font-medium text-slate-900 wamerce-dark:text-white">{active?.count || 0}</dd></div>
            <div><dt className="text-xs text-slate-500">Last contact</dt><dd className="font-medium text-slate-900 wamerce-dark:text-white">{fmtAgo(active?.last_at)} ago</dd></div>
            <div><dt className="text-xs text-slate-500">Tags</dt><dd className="flex flex-wrap gap-1 mt-1"><Pill>WhatsApp opt-in</Pill></dd></div>
          </dl>
        </aside>
      </div>
    </WamerceShell>
  );
}

function ChannelDot({ channel }) {
  const map = { whatsapp: 'bg-emerald-500', instagram: 'bg-gradient-to-br from-fuchsia-500 to-amber-400', facebook: 'bg-blue-500' };
  return <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${map[channel] || 'bg-slate-400'}`} />;
}

function Bubble({ side, text, meta }) {
  const me = side === 'me';
  return (
    <div className={`flex ${me ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] flex flex-col ${me ? 'items-end' : 'items-start'}`}>
        <span className={`px-4 py-2.5 rounded-2xl text-sm shadow-sm ${me ? 'bg-emerald-500 text-white rounded-br-sm' : 'bg-white wamerce-dark:bg-slate-800 text-slate-800 wamerce-dark:text-slate-200 rounded-bl-sm'}`}>{text}</span>
        {meta && <span className="text-[10px] text-slate-400 mt-1">{meta}</span>}
      </div>
    </div>
  );
}

function Pill({ children }) {
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 wamerce-dark:bg-slate-800 text-xs text-slate-700 wamerce-dark:text-slate-300">{children}</span>;
}

// ============ INVENTORY ============
export function WamerceInventory() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  useEffect(() => {
    fetch(`${API}/api/shopify/products?limit=50`).then((r) => r.json()).then((d) => {
      const arr = Array.isArray(d) ? d : d?.products || d?.data || [];
      setProducts(arr);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const filtered = products.filter((p) => !q || (p.title || p.name || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <WamerceShell active="inventory">
      <header className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div><h1 className="text-3xl font-bold tracking-tight text-slate-900 wamerce-dark:text-white">Inventory</h1><p className="text-slate-500 wamerce-dark:text-slate-400">Products, categories & brands from your connected stores.</p></div>
        <div className="flex items-center gap-2">
          <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="pl-9 h-10 bg-white wamerce-dark:bg-slate-800/60 border-slate-200 wamerce-dark:border-slate-700 w-72" data-testid="inv-search" /></div>
          <Button className="h-10 bg-emerald-500 hover:bg-emerald-400 text-white">+ Add product</Button>
        </div>
      </header>
      <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 overflow-hidden">
        {loading ? (
          <p className="p-10 text-center text-slate-500 wamerce-dark:text-slate-400">Loading products…</p>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center space-y-3"><Boxes className="w-12 h-12 mx-auto text-slate-300" /><p className="font-semibold text-slate-700 wamerce-dark:text-slate-200">{q ? 'No matches' : 'No products yet'}</p><p className="text-sm text-slate-500">{q ? 'Try a different search term' : 'Connect Shopify or import a CSV to populate your catalog.'}</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 wamerce-dark:bg-slate-800/40">
              <tr><th className="text-left px-5 py-3 font-medium">Product</th><th className="text-left px-5 py-3 font-medium">SKU / Handle</th><th className="text-left px-5 py-3 font-medium">Price</th><th className="text-left px-5 py-3 font-medium">Status</th><th className="text-right px-5 py-3 font-medium">Stock</th></tr>
            </thead>
            <tbody>
              {filtered.slice(0, 50).map((p, i) => (
                <tr key={p.id || p.handle || i} className="border-t border-slate-100 wamerce-dark:border-slate-800/60 hover:bg-slate-50/50 wamerce-dark:hover:bg-slate-800/30">
                  <td className="px-5 py-3 flex items-center gap-3">
                    {p.image || p.images?.[0]?.src ? <img alt="" src={p.image || p.images[0].src} className="w-10 h-10 rounded-md object-cover" /> : <div className="w-10 h-10 rounded-md bg-slate-100 wamerce-dark:bg-slate-800" />}
                    <span className="font-medium text-slate-900 wamerce-dark:text-white">{p.title || p.name || '—'}</span>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-500">{p.handle || p.sku || '—'}</td>
                  <td className="px-5 py-3">₹{p.price || p.variants?.[0]?.price || '—'}</td>
                  <td className="px-5 py-3"><Pill>{p.status || 'active'}</Pill></td>
                  <td className="px-5 py-3 text-right">{p.inventory_quantity ?? p.variants?.[0]?.inventory_quantity ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </WamerceShell>
  );
}

// ============ SETTINGS ============
export function WamerceSettings() {
  const [tab, setTab] = useState('workspace');
  const TABS = [
    { id: 'workspace', label: 'Workspace', icon: SetIcon },
    { id: 'team', label: 'Team', icon: Users2 },
    { id: 'integrations', label: 'Integrations', icon: Plug },
    { id: 'billing', label: 'Billing', icon: CreditCard },
  ];
  return (
    <WamerceShell active="settings">
      <header className="space-y-1 mb-6"><h1 className="text-3xl font-bold tracking-tight text-slate-900 wamerce-dark:text-white">Settings</h1><p className="text-slate-500 wamerce-dark:text-slate-400">Manage your workspace, team and integrations.</p></header>
      <div className="grid md:grid-cols-[200px_1fr] gap-6">
        <aside className="space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
                className={`w-full h-10 px-3 inline-flex items-center gap-2.5 rounded-lg text-sm font-medium transition-colors ${
                  tab === t.id ? 'bg-emerald-400/15 text-emerald-700 wamerce-dark:bg-emerald-400/15 wamerce-dark:text-emerald-300'
                              : 'text-slate-600 wamerce-dark:text-slate-400 hover:bg-slate-100 wamerce-dark:hover:bg-slate-800'
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </aside>
        <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 p-6">
          {tab === 'workspace' && <WorkspaceTab />}
          {tab === 'team' && <TeamTab />}
          {tab === 'integrations' && <IntegrationsTab />}
          {tab === 'billing' && <BillingTab />}
        </section>
      </div>
    </WamerceShell>
  );
}

function WorkspaceTab() {
  return (
    <div className="space-y-5 max-w-xl">
      <h2 className="text-lg font-semibold text-slate-900 wamerce-dark:text-white">Workspace</h2>
      <Field label="Workspace name" defaultValue="TNV Collection" />
      <Field label="Brand colour" defaultValue="#10b981" type="color" />
      <Field label="Time zone" defaultValue="Asia/Kolkata (UTC+5:30)" />
      <Button className="bg-emerald-500 hover:bg-emerald-400 text-white">Save changes</Button>
    </div>
  );
}
function TeamTab() {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between"><h2 className="text-lg font-semibold text-slate-900 wamerce-dark:text-white">Team</h2><Button className="bg-emerald-500 hover:bg-emerald-400 text-white">+ Invite agent</Button></div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500"><tr className="border-b border-slate-200 wamerce-dark:border-slate-800"><th className="text-left py-2 font-medium">Name</th><th className="text-left py-2 font-medium">Email</th><th className="text-left py-2 font-medium">Role</th><th className="text-left py-2 font-medium">Status</th></tr></thead>
        <tbody>
          <tr className="border-b border-slate-100 wamerce-dark:border-slate-800/60"><td className="py-3 font-medium">TNV Admin</td><td className="py-3 text-slate-500">admin@wamerce.com</td><td className="py-3">Admin</td><td className="py-3"><Pill>Live</Pill></td></tr>
        </tbody>
      </table>
    </div>
  );
}
function IntegrationsTab() {
  const ROW = [
    { name: 'Shopify', desc: 'Sync products, customers, orders', status: 'Connected' },
    { name: 'WhatsApp Business', desc: 'Send templates and receive replies', status: 'Connected' },
    { name: 'Instagram', desc: 'Reply to DMs from Instagram', status: 'Not connected' },
    { name: 'Facebook Page', desc: 'Reply to Page inbox', status: 'Not connected' },
    { name: 'Razorpay', desc: 'Accept online payments', status: 'Connected' },
    { name: 'Shri Maruti Logistics', desc: 'Auto-book orders for fulfillment', status: 'Connected' },
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-900 wamerce-dark:text-white">Integrations</h2>
      <ul className="divide-y divide-slate-100 wamerce-dark:divide-slate-800">
        {ROW.map((r) => (
          <li key={r.name} className="flex items-center justify-between py-3">
            <div><p className="font-medium text-slate-900 wamerce-dark:text-white">{r.name}</p><p className="text-xs text-slate-500">{r.desc}</p></div>
            {r.status === 'Connected' ? <Pill>Connected</Pill> : <Button size="sm" variant="outline" className="border-emerald-400 text-emerald-600 hover:bg-emerald-50">Connect</Button>}
          </li>
        ))}
      </ul>
    </div>
  );
}
function BillingTab() {
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-slate-900 wamerce-dark:text-white">Billing</h2>
      <div className="rounded-xl border-2 border-emerald-400/40 bg-emerald-50/40 wamerce-dark:bg-emerald-500/5 p-5 space-y-3">
        <div className="flex items-center justify-between"><p className="font-semibold text-emerald-700">Free plan</p><Pill>Active</Pill></div>
        <p className="text-sm text-slate-600 wamerce-dark:text-slate-300">500 contacts / month · WhatsApp Business · Inbox · Up to 1 store</p>
        <Button className="bg-emerald-500 hover:bg-emerald-400 text-white">Upgrade to Pro</Button>
      </div>
    </div>
  );
}
function Field({ label, defaultValue, type = 'text' }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-700 wamerce-dark:text-slate-300">{label}</label>
      <Input type={type} defaultValue={defaultValue} className="bg-slate-50 wamerce-dark:bg-slate-800/60 border-slate-200 wamerce-dark:border-slate-700" />
    </div>
  );
}

// ============ COMING SOON (kept for IG/FB/Others/WA, simple shell) ============
export function WamerceComingSoon({ section }) {
  const titleMap = {
    whatsapp: 'WhatsApp Console',
    instagram: 'Instagram Commerce',
    facebook: 'Facebook Commerce',
    others: 'More Channels',
  };
  return (
    <WamerceShell active={section}>
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center space-y-4">
        <span className="inline-flex w-14 h-14 rounded-2xl bg-emerald-50 wamerce-dark:bg-emerald-500/10 items-center justify-center"><Rocket className="w-6 h-6 text-emerald-500" /></span>
        <h1 className="text-3xl font-bold text-slate-900 wamerce-dark:text-white">{titleMap[section] || 'Coming soon'}</h1>
        <p className="text-slate-500 wamerce-dark:text-slate-400 max-w-md">We&apos;re building this section as part of the new Wamerce experience.</p>
        <Link to="/v2/dashboard" data-testid="back-to-dashboard" className="text-emerald-600 hover:underline text-sm font-medium">← Back to Dashboard</Link>
      </div>
    </WamerceShell>
  );
}

// Redirect /dashboard → /v2/dashboard (Phase C swap)
export function V2Redirect() { return <Navigate to="/v2/dashboard" replace />; }

// ============ SETUP WIZARD ============
export function WamerceSetupWizard() {
  const { stepId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiFetch(`/api/v2/setup/${stepId}`)
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Not found'); return r.json(); })
      .then((d) => { setConfig(d); setStepIdx(0); setFormData({}); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [stepId]);

  const Icon = STEP_ICON[stepId] || Rocket;
  const tint = { 'connect-shopify': 'violet', 'add-products': 'sky', 'setup-store': 'emerald', 'connect-instagram': 'rose', 'add-team': 'amber' }[stepId] || 'emerald';

  const submitFinal = async () => {
    setSubmitting(true);
    try {
      if (stepId === 'connect-shopify') {
        const shop = (formData.shop_domain || '').trim().toLowerCase();
        if (!shop || !shop.endsWith('.myshopify.com')) { toast.error('Enter a valid shop domain (e.g. yourshop.myshopify.com)'); setSubmitting(false); return; }
        window.location.href = `${API}/api/v2/oauth/shopify/install?shop=${encodeURIComponent(shop)}&business_id=${encodeURIComponent(bizId())}`;
        return;
      }
      if (stepId === 'connect-instagram') {
        window.location.href = `${API}/api/v2/oauth/instagram/install?business_id=${encodeURIComponent(bizId())}`;
        return;
      }
      if (stepId === 'add-team') {
        const r = await apiFetch('/api/v2/team/invite', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email, full_name: formData.full_name, role: formData.role || 'agent',
            can_send: !!formData.can_send, can_manage_inventory: !!formData.can_manage_inventory, can_invite: !!formData.can_invite,
          }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data?.detail || 'Failed to invite');
        if (data.delivery?.channel === 'resend') toast.success(`Invite emailed to ${formData.email}`);
        else toast.success(`Invite created. ${data.delivery?.note || ''}`.trim());
        navigate('/v2/dashboard');
        return;
      }
      const r = await apiFetch(`/api/v2/setup/${stepId}/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: formData }),
      });
      if (!r.ok) throw new Error('Failed to save');
      toast.success(`${config?.title} configured`);
      navigate('/v2/dashboard');
    } catch (e) { toast.error(e.message || 'Failed'); } finally { setSubmitting(false); }
  };

  if (loading) return <WamerceShell active="dashboard"><p className="text-center py-20 text-slate-500">Loading wizard…</p></WamerceShell>;
  if (error) return (
    <WamerceShell active="dashboard">
      <div className="max-w-md mx-auto py-20 text-center space-y-3">
        <X className="w-10 h-10 mx-auto text-rose-400" />
        <h2 className="text-lg font-semibold text-slate-900 wamerce-dark:text-white">{error}</h2>
        <Link to="/v2/dashboard" className="text-emerald-600 hover:underline text-sm">← Back to Dashboard</Link>
      </div>
    </WamerceShell>
  );

  const steps = config?.steps || [];
  const step = steps[stepIdx] || {};
  const isLast = stepIdx === steps.length - 1;

  return (
    <WamerceShell active="dashboard">
      <div className="max-w-2xl mx-auto">
        <Link to="/v2/dashboard" data-testid="wizard-back" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">← Back to Dashboard</Link>
        <header className="mb-8 flex items-start gap-4">
          <span className={`w-14 h-14 rounded-2xl inline-flex items-center justify-center shadow-md ${TINTS[tint]}`}><Icon className="w-6 h-6" /></span>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 wamerce-dark:text-white">{config.title}</h1>
            <p className="text-slate-500 wamerce-dark:text-slate-400">{config.subtitle}</p>
          </div>
        </header>

        <ol className="flex items-center gap-2 mb-8" data-testid="wizard-steps">
          {steps.map((s, i) => (
            <li key={s.id} className="flex items-center gap-2 flex-1">
              <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-bold transition-colors ${
                i < stepIdx ? 'bg-emerald-500 text-white' : i === stepIdx ? `${TINTS[tint]} ring-4 ring-emerald-500/15` : 'bg-slate-200 wamerce-dark:bg-slate-800 text-slate-500'
              }`}>{i < stepIdx ? <Check className="w-4 h-4" /> : i + 1}</span>
              {i < steps.length - 1 && <span className={`flex-1 h-0.5 rounded ${i < stepIdx ? 'bg-emerald-400' : 'bg-slate-200 wamerce-dark:bg-slate-800'}`} />}
            </li>
          ))}
        </ol>

        <section className="rounded-2xl bg-white wamerce-dark:bg-[#161826] border border-slate-200 wamerce-dark:border-slate-800 p-7 space-y-5" data-testid={`wizard-step-${stepIdx}`}>
          <div>
            <h2 className="text-xl font-semibold text-slate-900 wamerce-dark:text-white">{step.title}</h2>
            <p className="text-sm text-slate-500 wamerce-dark:text-slate-400 mt-1">{step.description}</p>
          </div>

          {step.fields && step.fields.map((f) => (
            <WizardField key={f.name} field={f} value={formData[f.name]}
              onChange={(v) => setFormData((d) => ({ ...d, [f.name]: v }))} />
          ))}

          {step.permissions && (
            <ul className="space-y-2 rounded-xl bg-slate-50 wamerce-dark:bg-slate-800/40 p-4">
              {step.permissions.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-slate-700 wamerce-dark:text-slate-300">
                  <Shield className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-slate-100 wamerce-dark:border-slate-800">
            <Button type="button" variant="ghost"
              onClick={() => stepIdx > 0 ? setStepIdx(stepIdx - 1) : navigate('/v2/dashboard')}
              data-testid="wizard-prev"
              className="text-slate-600 wamerce-dark:text-slate-400">
              {stepIdx > 0 ? '← Previous' : 'Cancel'}
            </Button>
            <Button type="button" data-testid="wizard-next"
              onClick={() => isLast ? submitFinal() : setStepIdx(stepIdx + 1)}
              disabled={submitting}
              className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold h-10 px-5">
              {submitting ? 'Saving…' : isLast ? (step.cta || 'Complete') : 'Continue →'}
            </Button>
          </div>
        </section>
      </div>
    </WamerceShell>
  );
}

function WizardField({ field, value, onChange }) {
  const v = value ?? field.default ?? (field.type === 'checkbox' ? false : '');
  if (field.type === 'select') {
    return (
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-slate-700 wamerce-dark:text-slate-300">{field.label}{field.required && <span className="text-rose-500 ml-1">*</span>}</label>
        <select value={v} onChange={(e) => onChange(e.target.value)}
          data-testid={`field-${field.name}`}
          className="w-full h-10 rounded-md bg-slate-50 wamerce-dark:bg-slate-800/60 border border-slate-200 wamerce-dark:border-slate-700 px-3 text-sm text-slate-900 wamerce-dark:text-white">
          <option value="" disabled>— Choose —</option>
          {field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <label className="flex items-center gap-2 cursor-pointer" data-testid={`field-${field.name}`}>
        <input type="checkbox" checked={!!v} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-emerald-500" />
        <span className="text-sm text-slate-700 wamerce-dark:text-slate-300">{field.label}</span>
      </label>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-slate-700 wamerce-dark:text-slate-300">{field.label}{field.required && <span className="text-rose-500 ml-1">*</span>}</label>
        <textarea value={v} onChange={(e) => onChange(e.target.value)} rows={3}
          data-testid={`field-${field.name}`}
          placeholder={field.placeholder}
          className="w-full rounded-md bg-slate-50 wamerce-dark:bg-slate-800/60 border border-slate-200 wamerce-dark:border-slate-700 px-3 py-2 text-sm text-slate-900 wamerce-dark:text-white resize-none" />
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-700 wamerce-dark:text-slate-300">{field.label}{field.required && <span className="text-rose-500 ml-1">*</span>}</label>
      <Input type={field.type || 'text'} value={v} onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        data-testid={`field-${field.name}`}
        className="bg-slate-50 wamerce-dark:bg-slate-800/60 border-slate-200 wamerce-dark:border-slate-700" />
    </div>
  );
}
